import { useEffect, useMemo, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { getHours } from "date-fns";
import {
  Box,
  Button,
  Divider,
  Link,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useListAndSearchAllSubscriptions } from "@rentablo/api/apiv1";
import styled from "@emotion/styled";
import { useMandatorContext } from "@rentablo/theming";
import { useNavigate } from "react-router";
import AddIcon from "@icons/myicons/plus.svg?react";
import { useFeatureFlag } from "@hooks";
import { useAccountHolders } from "@api/v1/hooks/useAccountHolder";
import { useMyself } from "@api/v1/hooks/useMyself";
import { AdaptiveButton, PageLayout } from "@components";
import { Banner, BottomControlsPanel } from "@rentablo/components";
import { useFundCommissions } from "@features/dashboard/hooks/useFundCommissions";
import { intervalToTimeRange } from "@helpers";
import { DepotsSelector } from "@features/selectDepots/DepotsSelector";
import { useDashboardSelectedGroupsContext } from "@features/dashboard/DashboardSelectedGroupProvider";
import { DashboardChartContainer } from "@features/dashboard/components/Dashboard/DashboardChartContainer";
import { AddPortfolio, AddPortfolioHandle } from "@features/add/AddPortfolio";
import { useAppLayout } from "@providers";
import { useInvestmentsChartDataTotal } from "../../hooks/chart/useInvestmentsChartDataTotal";
import { useInvestmentsCount } from "./hooks";
import { getNetfondsImportStatus } from "./utils/getNetfondsImportStatus";
import {
  Assets,
  DashboardPageNews,
  DashboardNotificationManager,
  CashbackBanner,
  DashboardPerformance,
} from "./components";
import { getWelcomePhrase } from "./utils";

const Container = styled(Box)`
  min-height: 620px;
  display: flex;
`;

const MetricsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  min-width: 366px;

  ${({ theme }) => theme.breakpoints.down("md")} {
    flex-direction: column-reverse;
    gap: ${({ theme }) => theme.spacing(5)};
    width: 100%;
    min-width: unset;
  }

  & > :not(style) {
    flex: 1;
  }
`;

const StyledDivider = styled(Divider)`
  border-color: ${({ theme }) => theme.palette.border.secondary};
`;

export const DashboardPage = () => {
  const { t } = useTranslation();
  const { sidebarCollapsed } = useAppLayout();
  const theme = useTheme();
  const navigate = useNavigate();
  const isLessMd = useMediaQuery(theme.breakpoints.down("md"));
  const isLess1200 = useMediaQuery(theme.breakpoints.down(1200));
  const isLess1450 = useMediaQuery(theme.breakpoints.down(1450));
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const showNewsAsFullBlock = sidebarCollapsed ? isLess1200 : isLess1450;
  const { mode, mandatorSlug } = useMandatorContext();
  const isCashbackEnabled = useFeatureFlag("CASHBACK_WIZARD");
  const { startDate, endDate } = intervalToTimeRange("oneYear");
  const addPortfolioRef = useRef<AddPortfolioHandle | null>(null);

  const { selectedGroup, accounts, groupedAccountIds, refetch, isFetching } =
    useDashboardSelectedGroupsContext();

  const accountIds = useMemo(
    () => [
      ...groupedAccountIds.cashIds,
      ...groupedAccountIds.creditCardIds,
      ...groupedAccountIds.depotIds,
    ],
    [groupedAccountIds]
  );

  const {
    statistic,
    currency,
    isFetching: isStatisticLoading,
  } = useInvestmentsChartDataTotal(accountIds, startDate!, endDate!);

  const { data: subscriptionsResponse } = useListAndSearchAllSubscriptions();

  const investmentsCount = useInvestmentsCount(
    accounts,
    groupedAccountIds.depotIds
  );

  const { data: accountHoldersResponse } = useAccountHolders();
  const accountHolders = accountHoldersResponse?.accountHolders ?? [];

  const { data: myselfData } = useMyself();
  const { data: fundCommissions } = useFundCommissions();

  useEffect(() => {
    if (!isFetching && accountIds?.length === 0)
      addPortfolioRef.current?.openModal();
  }, [isFetching, accountIds]);

  const availableCashbackSubscriptions =
    subscriptionsResponse?.subscriptions?.filter(
      ({ isCancelled }) => !isCancelled
    ) ?? [];

  const netfondsImportStatus = getNetfondsImportStatus(accountHolders);

  const totalValue = statistic?.portfolioValue ?? 0;
  const name = myselfData?.profileReference?.firstName;
  const welcomePhrase = getWelcomePhrase(getHours(new Date()));
  const welcomeText = name ? `${welcomePhrase}, ${name}` : welcomePhrase;

  const addButton = (
    <AddPortfolio
      ref={addPortfolioRef}
      mobileTitle={t("dashboardPage.dashboard.header.add")}
      anchor={
        isXs ? (
          <AdaptiveButton
            iconOnly
            icon={<AddIcon width="16px" height="16px" />}
            label={t("dashboardPage.dashboard.header.add")}
            testId="add-account"
          />
        ) : (
          <Button
            variant="primary"
            startIcon={<AddIcon width="18px" height="12px" />}
            data-testid="add-account"
          >
            {t("dashboardPage.dashboard.header.add")}
          </Button>
        )
      }
    />
  );

  return (
    <PageLayout
      isMainPage
      hasAddButton={!isXs}
      title={
        <Box>
          <Typography variant="body4" color="textSecondary">
            {welcomeText}
          </Typography>
          <Typography variant="h4" component="h1" mb={theme.spacing(2)}>
            {t("dashboardPage.header.title")}
          </Typography>
          <DepotsSelector
            selectedGroup={selectedGroup}
            selectedGroupParams={{ id: "dashboard" }}
            color="text.secondary"
            onSave={() => refetch()}
          />
        </Box>
      }
      actionButton={isXs ? undefined : addButton}
      hasBottomControlsPanel={isXs}
    >
      {mandatorSlug === "rentablo" &&
        mode === "light" &&
        !localStorage.getItem("MODE") && (
          <Banner
            type="info"
            displayDefaultIcon
            text={
              <Typography variant="body3" color="textSecondary">
                <Trans
                  i18nKey="dashboardPage.modeBanner.text"
                  components={{
                    linkText: (
                      <Link
                        onClick={() =>
                          navigate("/settings/profile?changeMode=true")
                        }
                      />
                    ),
                  }}
                />
              </Typography>
            }
            hideable
            id="dashboard-mode-banner"
            mb={6}
            timePointToHide={new Date("2025-01-01")}
          />
        )}
      <DashboardNotificationManager />

      {isCashbackEnabled && (
        <CashbackBanner
          subscriptions={subscriptionsResponse?.subscriptions}
          fundCommissions={fundCommissions}
        />
      )}

      <Container
        flexDirection={
          isLessMd || (isLess1200 && !sidebarCollapsed) ? "column" : "row"
        }
        gap={isLessMd || (isLess1200 && !sidebarCollapsed) ? 4 : 5}
      >
        <DashboardChartContainer
          totalValue={totalValue ?? 0}
          statistic={statistic}
          currency={currency ?? "EUR"}
          isStatisticsLoading={isStatisticLoading}
          showNews={!showNewsAsFullBlock}
        />
        <MetricsContainer>
          <DashboardPerformance
            showCompare
            showDividendsToggle
            showCapitalToggle
            chartHeight={356}
          />
        </MetricsContainer>
        {isLessMd && <StyledDivider />}
      </Container>

      <Assets
        investmentsCount={investmentsCount}
        fundCommissions={fundCommissions}
        cashAccountsBalance={statistic?.cashBalance ?? 0}
        currency={currency ?? "EUR"}
        subscriptions={subscriptionsResponse?.subscriptions}
        availableCashbackSubscriptions={availableCashbackSubscriptions}
        netfondsImportStatus={netfondsImportStatus}
        openAddModal={() => {
          addPortfolioRef.current?.onSelect({
            default: "02_cash",
            type: "",
          });
        }}
      />

      {showNewsAsFullBlock && <DashboardPageNews mode="full" />}
      {isXs && (
        <BottomControlsPanel actions={addButton}>{null}</BottomControlsPanel>
      )}
    </PageLayout>
  );
};
